/**
 * Single source of truth for chart and accent colors.
 *
 * Why this exists: nine pages each redeclared their own
 * `BRAND_COLORS` const with the same brand hexes plus drifting
 * chart-only extras (`emerald` here, `green` there, `blue` here as
 * `#3B82F6` and there as `#4F7DF3`). Charts on different dashboards
 * looked subtly inconsistent for the same metric. The audit
 * (docs/design-review-2026-05.md, since archived — redesign reverted #631) called this out as P0; this
 * module is the consolidation.
 *
 * Brand hexes (`primary`, `secondary`, `accent`, `lightBlue`,
 * `lightPurple`) mirror tailwind.config.js — keep them in sync.
 * Anything outside that core set is chart-only — UI surfaces should
 * use the Tailwind palette tokens (`bg-primary-500`, `text-accent-700`,
 * etc.) and never reach for these hexes directly. The ESLint rule
 * in .eslintrc.json (no-restricted-syntax banning `bg-[#…]` etc.)
 * keeps that guardrail honest going forward.
 */
export const BRAND_COLORS = {
  // Brand chrome — must match tailwind.config.js
  primary: '#000056',     // Deep navy   (primary.500)
  secondary: '#F92769',   // Hot pink    (secondary.500)
  accent: '#9328FF',      // Vibrant purple (accent.500)
  lightBlue: '#E9F3FE',   // light.500
  lightPurple: '#C8C8F4', // accent-tinted highlight

  // Chart-only categorical fills. Any non-brand color used in a
  // Recharts <Cell/> or similar visualization belongs here. Do NOT
  // reach for these for UI chrome — there is a Tailwind token for
  // every UI use case.
  blue: '#4F7DF3',
  green: '#10B981',
  emerald: '#10B981',     // alias kept for migration; same hex
  purple: '#8B5CF6',
  orange: '#F59E0B',
  amber: '#F59E0B',       // alias kept for migration; same hex
  pink: '#EC4899',
  cyan: '#06B6D4',
  red: '#EF4444',
  teal: '#14B8A6',
  indigo: '#6366F1',
} as const

export type BrandColorKey = keyof typeof BRAND_COLORS
